<template>
  <div v-if="effectiveType === EFFECTIVE_TYPES.SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Sales Basis is a view into what was sold in the date range, independent of when (or if) it was
      paid and independent of when it is effective (i.e. what the showing date is).
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.CASH_BASIS">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Cash Basis
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.PAID" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.REFUNDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Cash Basis is a view into things that were Paid or Refunded during the date range, independent
      of when it was placed and independent of when it is Effective.
      <br><br>
      This is used for reconciling against all payments for the day and for reconciling cash deposits and credit card deposits.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.ACCRUAL">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Accrual
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_SALES" />
      <q-icon
        class="col-auto q-mx-xs"
        name="add"
        color="grey-8"
        size="md"
      />
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_SALES" />
      <q-icon
        class="col-auto q-mx-xs"
        name="add"
        color="grey-8"
        size="md"
      />
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.PAST_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.PAST_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Accrual is a view into things that are effective during the date range, i.e. the ticket is for a showing in the
      date range, or food due in the date range. This view is designed for determining when revenue is earned.
      <br><br>
      This value is independent of when the order was placed (e.g. current, past, future) and when the order way paid (and even if it was paid).
      <br><br>
      This value includes all voids and sales effective in the date range placed before or during the date range. Voids and sales that
      happen after the effective date are not included in this value. They are instead accounted for when they happen. e.g. Someone
      sells a ticket for a past showing or voids a ticket for a past showing. This will be account for when that transaction occurred and
      will not change the value of the effective date of the showing. The reason for this treatment is to ensure this value
      does not change over time with future voids or sales, making it ideal for accounting purposes.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.ATTENDANCE">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Attendance
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_SALES" />
      <q-icon
        class="col-auto q-mx-xs"
        name="add"
        color="grey-8"
        size="md"
      />
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_SALES" />
      <q-icon
        class="col-auto q-mx-xs"
        name="add"
        color="grey-8"
        size="md"
      />
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.RETROACTIVE_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.RETROACTIVE_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Attendance is a view into things that are effective during the date range, i.e. the ticket is for a showing in the
      date range, or food due in the date range.
      <br><br>
      This value is independent of when the order was placed (e.g. current, past, future) and when the order way paid (and even if it was paid).
      <br><br>
      All voids and sales, even ones after the date range, impact this value. This is in contrast to Effective
      where voids and sales that happen after the date range do not impact the value.
      <br><br>
      This makes this value strictly more accurate, but also means it can change with future actions. A void or sale in the
      future will change this value. For this reason, this is useful for management reporting, but not for accounting.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.CURRENT_SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Current Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Current Sales is useful for accrual accounting. This is a view into things that are
      both Effective and Placed during the date range. These are people who came and bought a
      ticket today and used it today. For many cinemas, this makes up the vast majority of their sales.
      <br><br>
      This is used in conjunction with Maturing and Future Sales to reconcile against all sales for the day.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.MATURING_SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Maturing Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.MATURING_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      The Maturing Sales Effective Type is useful for accrual accounting. This is a view into things that
      are Effective in the date range and were placed in the past. These are people who came and bought a
      ticket for a future date.
      <br><br>
      Maturing sales are things that were classified as Future Sales for a prior period.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.FUTURE_SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Future Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.FUTURE_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.FUTURE_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Future Sales is useful for accrual accounting. This is a view into things that
      were placed in the date range and are effective in future. These are people who came and bought a
      ticket for a future date.
      <br><br>
      Future sales are things that will be classified as Maturing Sales for a future period.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.PAST_SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Past Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.PAST_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.PAST_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Past Sales is useful for accrual accounting. This is a view into things that
      are effective in the past range and were placed in this date range. This is when someone
      purchases or voids a ticket for a past showing. This is much less common than Current/Future/Maturing sales.
      <br><br>
      Past Sales are things that are classified as Retroactive Sales for a prior period.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.RETROACTIVE_SALES">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Retroactive Sales
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.RETROACTIVE_SALES" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.RETROACTIVE_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Retroactive Sales is useful for accrual accounting. This is a view into things that
      are Effective in the date range and were placed in a future range. This is when someone
      purchases or voids a ticket for a past showing. This is much less common than Current/Future/Maturing sales.
      <br><br>
      Retroactive Sales are things that are classified as Past Sales for a future period.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.CURRENT_PAYMENTS">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Current Payments
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_PAYMENTS" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.REFUNDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Current Payments is view into things that are paid and placed during the date range.
      This is the normal scenario for non-accounts receivable orders. When this is not the case,
      the order is a part of the accounts receivable.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.UNPAID_PLACEMENTS">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Unpaid Placements
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_UNPAID_PLACEMENTS" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_UNPAID_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.PAST_UNPAID_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Unpaid Placements is view into things that are placed during the date range but not paid.
      These are either Tab or Invoice orders.
      <br><br>
      These are order that entered Accounts Receivable in the date range.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.PAID_PAST_PLACEMENTS">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Pay Past Placements
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.PAID_PAST_PLACEMENTS" />
    </div>
    <div class="text-body2 q-mt-lg">
      Pay Past Placements is view into things that are placed in the past and paid
      during the date range. These are either Tab or Invoice orders.
      <br><br>
      These are order that leave Accounts Receivable in the date range.
    </div>
  </div>

  <div v-else-if="effectiveType === EFFECTIVE_TYPES.ACCOUNTS_RECEIVABLE">
    <div class="row q-mb-lg">
      <div class="col-auto text-h5">
        Accounts Receivable
      </div>
      <q-space />
      <q-toggle
        v-if="showVoidControl"
        v-model="showVoid"
        label="Show Void Handling"
      >
        <q-tooltip>Show how voids are handled for this Effective Type</q-tooltip>
      </q-toggle>
    </div>
    <div class="row justify-center items-center">
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.PAST_UNPAID_PLACEMENTS" />
      <q-icon
        class="col-auto q-mx-xs"
        name="add"
        color="grey-8"
        size="md"
      />
      <effective-scenario :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_UNPAID_PLACEMENTS" />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.PAST_UNPAID_VOIDS"
      />
      <q-icon
        v-if="showVoid"
        class="col-auto q-mx-xs"
        name="remove"
        color="grey-8"
        size="md"
      />
      <effective-scenario
        v-if="showVoid"
        :effective-scenario="EFFECTIVE_SCENARIOS.CURRENT_UNPAID_VOIDS"
      />
    </div>
    <div class="text-body2 q-mt-lg">
      Accounts Receivable is all unpaid items as of the end of the date range. This is cumulative over all time.
      <br><br>
      Whereas Unpaid Placements and Paid Past Placements represent the change in Accounts Receivable for
      the date range, Accounts Receivable is the total amount of unpaid items as of the end of the
      range.
      <br><br>
      The start date does not impact this value, only the end date does.
    </div>
  </div>
</template>

<script>
import { EFFECTIVE_TYPES } from 'src/models/accounting/AccountSource'
import EffectiveScenario, { EFFECTIVE_SCENARIOS } from 'src/modules/admin/effective-type/EffectiveScenario'

export default {
  name: 'EffectiveTypeDetail',
  components: { EffectiveScenario },
  props: {
    effectiveType: {
      type: String,
      required: false,
      default: EFFECTIVE_TYPES.SALES
    },
    showVoidControl: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      showVoid: true
    }
  },
  created () {
    _.extend(this, {
      EFFECTIVE_TYPES,
      EFFECTIVE_SCENARIOS
    })
  }
}

</script>
